import React, { useCallback, useEffect, useState } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { useTranslation } from "react-i18next";
import { getTasks, updateTask } from "../../../services/taskServices";
import { Table, Dropdown, Menu } from "antd";
import CreateTaskModal from "../../../components/modelpopup/CreateTaskModal";
import api from "../../../api/axios";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import MultiAssignModal from "../../../components/modelpopup/MultiAssignModal";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import Loader from "../../../components/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import { encrypt } from "../../../utils/crypto";

const CreateTask = ({ showAssignments = false, filter = {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { createTask } = location.state || {};
  const { t } = useTranslation();
  const loginEmployeeId = localStorage.getItem("employeeID");
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(
    createTask ?? false
  );
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedMode, setSelectedMode] = useState("create");
  const [loader, setLoader] = useState(false);
  const [isMultiAssignModalOpen, setIsMultiAssignModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const openCreateTask = (detail, mode = "create") => {
    setIsCreateTaskModalOpen(true);
    setSelectedMode(mode);
    if (detail) {
      setSelectedTask(detail);
    }
  };
  const closeCreateTaskModal = () => {
    setIsCreateTaskModalOpen(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  const openMultiAssignModal = (task) => {
    setSelectedTask(task);
    setIsMultiAssignModalOpen(true);
  };

  const closeMultiAssignModal = () => {
    setIsMultiAssignModalOpen(false);
    setSelectedTask(null);
    setRefreshTrigger((prev) => prev + 1);
  };
  const [allTasks, setAllTasks] = useState([]);
  const [assignmentHistory, setAssignmentHistory] = useState({});  const fetchTasks = useCallback(async () => {
    try {
      const filterQuery = {
        ...filter,
        [showAssignments ? "assigned_to" : "created_by"]: loginEmployeeId,
      };

      const response = await getTasks(filterQuery);
      if (!response.success) {
        throw new Error("Network response was not ok");
      }
      
      const tasks = response?.tasks ?? [];
      setAllTasks(tasks);
      
      // Fetch assignment history for each task
      const historyPromises = tasks.map(async (task) => {
        if (task.task_id && showAssignments) {
          try {
            const historyResponse = await api.get(`tasks/${task.task_id}/assignments`);
            const allAssignments = historyResponse.data?.assignments || [];
            // Sort by assigned_at timestamp, most recent first
            const sortedHistory = allAssignments.sort((a, b) => (b.assigned_at || 0) - (a.assigned_at || 0));
            // Get all assignments except the current user's assignment
            const otherAssignments = sortedHistory.filter(assignment => 
              assignment.assigned_to.toString() !== loginEmployeeId
            );
            return { taskId: task.task_id, history: otherAssignments };
          } catch (error) {
            console.error(`Error fetching assignments for task ${task.task_id}:`, error);
            return { taskId: task.task_id, history: [] };
          }
        }
        return { taskId: task.task_id, history: [] };
      });
      
      const historyResults = await Promise.all(historyPromises);
      const historyMap = {};
      historyResults.forEach(({ taskId, history }) => {
        historyMap[taskId] = history;
      });
      setAssignmentHistory(historyMap);
      
    } catch (error) {
      console.error("Error fetching tasks:", error);
      return setAllTasks([]);
    }
  }, [filter, showAssignments, loginEmployeeId]);

  // Initial fetch and when dependencies change
  useEffect(() => {
    fetchTasks();
  }, [refreshTrigger]);

  // Handle modal state changes
  useEffect(() => {
    if (!isCreateTaskModalOpen) {
      setSelectedTask(null);
    }
  }, [isCreateTaskModalOpen]);

  const onDelete = async () => {
    setLoader(true);
    try {
      const response = await updateTask(selectedTask?.task_id, {
        status: 0,
        updated_by: loginEmployeeId,
      });
      if (response.success) {
        setRefreshTrigger((prev) => prev + 1);
      }
      toast.success(t("Task has been deleted successfully."));
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(t("Something went wrong while deleting the task."));
    } finally {
      setLoader(false);
      setSelectedTask(null);
      setIsDeleteModalOpen(false);
    }
  };

  const handleTaskTitleClick = (taskId) => {
    const encryptedTaskId = encrypt(taskId.toString());
    navigate(`id=${encryptedTaskId}`);
  };

  const handleTaskStatusUpdate = async (assignmentId, newStatus) => {
    if (!assignmentId) {
      console.error("No assignment ID provided");
      return;
    }
    
    setLoader(true);
    try {
      const response = await api.put(`tasks/assignment/${assignmentId}`, {
        task_status: newStatus
      });
      
      if (response.data.success) {
        toast.success(t("Task status updated successfully."));
        // Refresh the tasks list
        setRefreshTrigger((prev) => prev + 1);
      } else {
        throw new Error(response.data.message || "Failed to update task status");
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      toast.error(t("Failed to update task status. Please try again."));
    } finally {
      setLoader(false);
    }
  };

  // Status options like TaskDetails
  const taskStatusOptions = [
    { key: "ASSIGNED", label: t("Assigned"), bg: "warning" },
    { key: "IN_PROGRESS", label: t("In Progress"), bg: "primary" },
    { key: "COMPLETED", label: t("Completed"), bg: "success" }
  ];

  const paymentStatusOptions = [
    { key: "PENDING", label: t("Pending"), bg: "warning" },
    { key: "COMPLETED", label: t("Completed"), bg: "success" },
    { key: "PAID", label: t("Paid"), bg: "success" }
  ];

  const getTaskStatusBadge = (status, assignmentId, taskId) => {
    const statusValue = status || 'ASSIGNED';
    const currentStatus = taskStatusOptions.find(opt => opt.key === statusValue) || 
                         { key: statusValue, label: statusValue, bg: "secondary" };
    
    // Get assignment history for this task
    const taskHistory = assignmentHistory[taskId] || [];
    
    const statusMenu = (
      <Menu
        onClick={({ key }) => handleTaskStatusUpdate(assignmentId, key)}
      >
        {/* Current status options */}
        {taskStatusOptions
          .filter(option => option.key !== statusValue)
          .map(option => (
            <Menu.Item key={option.key}>
              <span className={`badge bg-${option.bg} me-2`}>{option.label}</span>
            </Menu.Item>
          ))}
      </Menu>
    );

    return (
      <Dropdown overlay={statusMenu} trigger={['click']} disabled={loader}>
        <span className={`badge bg-${currentStatus.bg}`} style={{ cursor: 'pointer' }}>
          {currentStatus.label}
          <i className="fa fa-caret-down ms-1"></i>
        </span>
      </Dropdown>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusValue = status || 'PENDING';
    const currentStatus = paymentStatusOptions.find(opt => opt.key === statusValue) || 
                         { key: statusValue, label: statusValue, bg: "secondary" };
    
    return (
      <span className={`badge bg-${currentStatus.bg}`}>
        {currentStatus.label}
      </span>
    );
  };

  const columns = [
    {
      title: "#",
      render: (_, __, index) => <span>{index + 1}</span>,
      key: "index",
      width: 50,
    },
    {
      title: t("Task Title"),
      dataIndex: "title",
      key: "title",
      render: (text, record) => {
        // Remove link if user is viewing their assigned tasks (showAssignments = true)
        const shouldShowLink = !showAssignments;
        
        return shouldShowLink ? (
          <button
            className="p-0 text-start text-primary fw-medium"
            style={{ border: "none", background: "none" }}
            onClick={() => handleTaskTitleClick(record.task_id)}
            title={t("Click to view task details")}
          >
            {text}
          </button>
        ) : (
          <span className="text-start fw-medium">{text}</span>
        );
      },
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) => {
        if (text.length > 50) {
          return (
            <button
              className="btn p-0"
              onClick={() =>
                Swal.fire({
                  title: "Task Description",
                  text: text,
                  showCloseButton: true,
                  showConfirmButton: false,
                  customClass: {
                    closeButton: "btn btn-lg btn-danger me-1 mt-1",
                    content: "text-start",
                  },
                })
              }
            >
              {text.substring(0, 50)}...
            </button>
          );
        }
        return <span>{text}</span>;
      },
    },
    {
      title: t("Priority"),
      dataIndex: "priority",
      key: "priority",
      render: (text) => {
        const priorityColors = {
          high: "danger",
          medium: "warning",
          low: "success",
        };
        return (
          <span className={`badge bg-${priorityColors[text]}`}>{text}</span>
        );
      },
    },
    {
      title: t("Deadline"),
      dataIndex: "deadline",
      key: "deadline",
      render: (text) => <span>{new Date(text).toLocaleDateString()}</span>,
    },
    // Add Payment Status column only when showAssignments is true (daily tasks view)
    ...(showAssignments ? [
      {
        title: t("Payment Status"),
        dataIndex: "payment_status",
        key: "payment_status",
        align: "center",
        width: 140,
        render: (text, record) => {
          const status = record.assignments?.[0]?.payment_status || record.payment_status || "PENDING";
          return getPaymentStatusBadge(status.toUpperCase());
        },
      },
      {
        title: t("Task Status"),
        dataIndex: "task_status",
        key: "task_status",
        align: "center",
        width: 140,
        render: (text, record) => {
          const currentStatus = record.assignments?.[0]?.task_status || record.task_status || "ASSIGNED";
          const assignmentId = record.assignments?.[0]?.assignment_id;
          
          return getTaskStatusBadge(currentStatus.toUpperCase(), assignmentId, record.task_id);
        },
      }
    ] : []),
    // {
    //   title: t("Task Status"),
    //   dataIndex: "task_status",
    //   key: "task_status",
    //   render: (text, record) => {
    //     const statusInfo = getStatusInfo(text);
    //     return (
    //       <Dropdown
    //         overlay={statusMenu(record)}
    //         trigger={["click"]}
    //         disabled={loader}
    //       >
    //         <span className={`btn badge btn-${statusInfo.className}`}>
    //           {statusInfo.label}
    //           <i class="fa-solid fa-caret-down ms-1"></i>
    //         </span>
    //       </Dropdown>
    //     );
    //   },
    // },
    // !showAssignments
    //   ? {
    //       title: t("Assigned To"),
    //       dataIndex: "assigned_to",
    //       key: "assigned_to",
    //       render: (text, record) => {
    //         if (text) {
    //           return (
    //             <button
    //               className="btn p-0"
    //               onClick={() => openCreateTask(record, "assign")}
    //             >
    //               <span className="badge bg-info">
    //                 {employees.find((employee) => employee.key == text)
    //                   ?.label || "Unassigned"}
    //               </span>
    //             </button>
    //           );
    //         } else {
    //           return (
    //             <button
    //               className="btn p-0"
    //               onClick={() => openCreateTask(record, "assign")}
    //             >
    //               <span className="badge bg-secondary">Unassigned</span>
    //             </button>
    //           );
    //         }
    //       },
    //     }
    //   : {},
    // {
    //   title: t("Payment Status"),
    //   dataIndex: "payment_status",
    //   key: "payment_status",
    //   render: (text, record) => {
    //     const paymentColors = {
    //       paid: "success",
    //       pending: "warning",
    //     };
    //     return record?.assigned_to && !showAssignments ? (
    //       <button
    //         className="btn p-0"
    //         onClick={() => openCreateTask(record, "payment")}
    //       >
    //         <span className={`badge bg-${paymentColors[text]}`}>{text}</span>
    //       </button>
    //     ) : showAssignments ? (
    //       <span className={`badge bg-${paymentColors[text]}`}>{text}</span>
    //     ) : (
    //       <>-</>
    //     );
    //   },
    // },
    !showAssignments
      ? {
          title: t("Actions"),
          key: "actions",
          render: (_, record) => (
            <div className="btn-group" role="group">
              <button
                className="btn btn-sm btn-outline-success"
                onClick={() => openCreateTask(record, "edit")}
                disabled={record?.payment_status === "paid" ? true : false}
                title={t("Edit Task")}
              >
                <i className="fa fa-pencil"></i>
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => openMultiAssignModal(record)}
                disabled={record?.payment_status === "paid" ? true : false}
                title={t("Assign Employee")}
              >
                <i className="fa fa-users"></i>
              </button>
              <button
                disabled={record?.assignments?.length > 0}
                className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  setSelectedTask(record);
                  setIsDeleteModalOpen(true);
                }}                
                title={t("Delete Task")}
              >
                <i className="fa fa-trash"></i>
              </button>
            </div>
          ),
        }
      : {},
  ];

  return (
    <div className={`${showAssignments ? "" : "page-wrapper "}`}>
      <div
        className={`${
          showAssignments ? "" : "content container-fluid position-relative"
        }`}
      >
        {!showAssignments ? (
          <Breadcrumbs
            maintitle={`Create Daily Task`}
            title="Dashboard"
            subtitle="Create Daily Task"
            name={t("Create Daily task")}
            openModal={openCreateTask}
            page="create-task"
            action="create"
          />
        ) : (
          <></>
        )}
        <div className="table-responsive mb-2">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={allTasks}
            rowKey={(record) => record.id}
            scroll={{ x: true }}
            tableLayout="auto"
          />
        </div>
      </div>
      {isCreateTaskModalOpen && (
        <CreateTaskModal
          show={isCreateTaskModalOpen}
          handleClose={() => closeCreateTaskModal()}
          handleOpen={() => openCreateTask()}
          taskDetails={selectedTask}
          mode={selectedMode}
        />
      )}
      {isMultiAssignModalOpen && (
        <MultiAssignModal
          show={isMultiAssignModalOpen}
          handleClose={closeMultiAssignModal}
          taskDetails={selectedTask}
        />
      )}
      <DeleteModal
        show={isDeleteModalOpen}
        Name="Delete Task"
        confirmDelete={onDelete}
        btnName="Delete"
        onClose={() => {
          setSelectedTask(null);
          setIsDeleteModalOpen(false);
        }}
      />
      {loader && <Loader show={loader} />}
    </div>
  );
};
export default CreateTask;
